import { defineMutator } from "@rocicorp/zero";
import { z } from "zod";
import { zql } from "../schema";
// eslint-disable-next-line no-restricted-imports -- legacy junk-drawer file; rename in plans/cached-singing-fiddle.md
import {
  requireWorkspaceRole,
  normalizeWorkspaceSlug,
  assertWorkspaceSlugAvailable,
} from "./helpers";
import { makeWorkspaceId, makeUserId } from "../types/branded-ids";
import { communitySettingsSchema } from "../types/community-settings";
import {
  workspaceMemberDeleteCore,
  workspaceMemberDeleteSchema,
  workspaceMemberUpdateCore,
  workspaceMemberUpdateSchema,
} from "./workspace-members-cores";

// Re-exported so the public surface of `./workspaces` is unchanged after
// the member-cores extraction: the barrel (`mutators/index.ts`) and the
// server-mutator overrides (`server/server-mutators-workspaces.ts`) import
// these names from here.
export {
  workspaceMemberDeleteCore,
  workspaceMemberDeleteSchema,
  type WorkspaceMemberDeleteResult,
  workspaceMemberUpdateCore,
  workspaceMemberUpdateSchema,
  type WorkspaceMemberUpdateResult,
} from "./workspace-members-cores";

export const workspacesMutators = {
  insert: defineMutator(
    z.object({
      id: z.string(),
      name: z.string(),
      slug: z.string(),
      logo_url: z.string().optional().nullable(),
      access_mode: z.string().optional(),
      allowed_email_domains: z.array(z.string()).optional().nullable(),
      retention_days: z.number().optional().nullable(),
      retention_applies_to_files: z.boolean().optional(),
      retention_applies_to_pins: z.boolean().optional(),
      // WS-B1: workspace authority region. Set by the region-selector UI
      // (WS-B13a, geo-IP-defaulted). Defaults to creator's home_region or
      // 'us' until that ships.
      region: z.string().optional(),
      created_by: z.string(),
      created_at: z.number(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.created_by !== ctx.sub) {
        throw new Error("Unauthorized: created_by mismatch");
      }
      if (tx.location === "server") {
        // Insert is for creation only. If a workspace with this id already
        // exists, return early without re-upserting. Modifications must go
        // through workspaces.update which has admin+ auth via
        // requireWorkspaceRole. This blocks two attack paths via upsert:
        //   1. Cross-workspace metadata takeover (different created_by)
        //   2. Demoted/removed founder overwriting workspace metadata
        //      (created_by is immutable so it can't be a proxy for
        //      current authority)
        // Legitimate retries are still safe: the existing row already has
        // the original data, so the no-op return matches the client's
        // optimistic state.
        const existing = await tx.run(
          zql.workspaces.where("id", args.id).one(),
        );
        if (existing) {
          return;
        }
      }
      const normalizedSlug = normalizeWorkspaceSlug(args.slug);
      const workspaces = await tx.run(zql.workspaces);
      assertWorkspaceSlugAvailable(workspaces, normalizedSlug, args.id);
      await tx.mutate.workspaces.upsert({
        ...args,
        slug: normalizedSlug,
        access_mode: args.access_mode ?? "invitation",
        // New workspaces default to private; owner flips public mode
        // from access-tab after creation. Keeping it false at insert
        // time also satisfies the PG CHECK constraint
        // `workspaces_public_policy_consistent` (policy must be NULL
        // when is_public=false).
        is_public: false,
        retention_applies_to_files: args.retention_applies_to_files ?? true,
        retention_applies_to_pins: args.retention_applies_to_pins ?? true,
        region: args.region ?? "us",
      });
    },
  ),
  update: defineMutator(
    z.object({
      id: z.string(),
      name: z.string().optional(),
      slug: z.string().optional(),
      logo_url: z.string().optional().nullable(),
      access_mode: z.string().optional(),
      allowed_email_domains: z.array(z.string()).optional().nullable(),
      is_public: z.boolean().optional(),
      public_join_policy: z.enum(["auto", "request"]).optional().nullable(),
      // Owner-tunable public-workspace community config (migration 154).
      // Admin+ gated by the requireWorkspaceRole check below; pass the JS
      // object directly (Zero serializes JSONB — never JSON.stringify).
      community_settings: communitySettingsSchema.optional().nullable(),
      retention_days: z.number().optional().nullable(),
      retention_applies_to_files: z.boolean().optional(),
      retention_applies_to_pins: z.boolean().optional(),
    }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        await requireWorkspaceRole(
          tx,
          makeWorkspaceId(args.id),
          makeUserId(ctx.sub),
        );
      }
      const updateArgs = { ...args };
      // Mirror the PG CHECK constraint at the mutator boundary so the
      // optimistic client write doesn't briefly flip a workspace into
      // a state the server will then reject. is_public=true requires a
      // policy; is_public=false must null the policy. Partial public-mode
      // updates inherit the current row before validating the pair.
      if (
        args.is_public !== undefined ||
        args.public_join_policy !== undefined
      ) {
        const current = await tx.run(zql.workspaces.where("id", args.id).one());
        const isPublic = args.is_public ?? current?.is_public ?? false;
        const currentPolicy =
          current?.public_join_policy === "auto" ||
          current?.public_join_policy === "request"
            ? current.public_join_policy
            : null;
        const policy =
          args.is_public === false
            ? null
            : args.public_join_policy !== undefined
              ? args.public_join_policy
              : currentPolicy;
        if (isPublic && !policy) {
          throw new Error(
            "public_join_policy is required when is_public is true",
          );
        }
        if (!isPublic && policy) {
          throw new Error(
            "public_join_policy must be null when is_public is false",
          );
        }
        updateArgs.is_public = isPublic;
        updateArgs.public_join_policy = policy;
      }
      // Enforce the community invariant: search_indexing ⟹ anonymous_view
      // (you cannot index what a logged-out visitor can't see). The UI enables
      // them together; mirror it here so a direct or optimistic write can't
      // persist an inconsistent blob that the middleware would index against
      // empty content endpoints.
      if (args.community_settings?.search_indexing) {
        updateArgs.community_settings = {
          ...args.community_settings,
          anonymous_view: true,
        };
      }
      if (typeof args.slug === "string") {
        const normalizedSlug = normalizeWorkspaceSlug(args.slug);
        const workspaces = await tx.run(zql.workspaces);
        assertWorkspaceSlugAvailable(workspaces, normalizedSlug, args.id);
        await tx.mutate.workspaces.update({
          ...updateArgs,
          slug: normalizedSlug,
        });
        return;
      }

      await tx.mutate.workspaces.update(updateArgs);
    },
  ),
  delete: defineMutator(z.object({ id: z.string() }), async () => {
    // Server-authoritative — deletes go through delete-routes.ts
    throw new Error("Unauthorized: use server delete endpoint");
  }),
};

export const workspaceMembersMutators = {
  insert: defineMutator(
    z.object({
      id: z.string(),
      workspace_id: z.string(),
      user_id: z.string(),
      role: z.string(),
      joined_at: z.number(),
    }),
    async () => {
      // Hard-reject the public workspace_members.insert mutator on
      // both client and server. There is no legitimate caller in the
      // codebase — the two real entry points are:
      //
      //   1. Workspace bootstrap → createWorkspaceBootstrap action,
      //      which uses tx.mutate.workspace_members.insert directly
      //      and bypasses this auth check entirely.
      //   2. Adding members to an existing workspace → the workspace
      //      invite accept flow (acceptWorkspaceInvite), which inserts
      //      the row only after the recipient consents.
      //
      // Allowing the public mutator opens two attack paths:
      //   - Row-ID hijack: an attacker upserts over a victim's PK,
      //     replacing user_id and silently kicking the victim
      //     (including primary_owner, bypassing the
      //     "primary_owner cannot leave" guard in delete).
      //   - Bootstrap-after-demotion: a workspace creator who has
      //     been demoted to member/collaborator can self-delete,
      //     then re-insert through the bootstrap path with role
      //     forced to "owner", silently restoring themselves.
      throw new Error(
        "Unauthorized: use createWorkspaceBootstrap action or server API",
      );
    },
  ),
  update: defineMutator(workspaceMemberUpdateSchema, async (params) => {
    await workspaceMemberUpdateCore(params);
  }),
  delete: defineMutator(workspaceMemberDeleteSchema, async (params) => {
    await workspaceMemberDeleteCore(params);
  }),
};
